import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Coins, Gauge, Boxes, FileSpreadsheet, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Κοστολόγηση Μεταλλικών Εξαρτημάτων',
      subtitle: 'Υπολογίστε το κόστος κάθε μεταλλικού εξαρτήματος πριν δώσετε τιμή: βάρος υλικού, κοπή laser ή ψαλίδι, στράντζα, σετάρισμα και κέρδος, απευθείας από DXF ή STEP.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε Τιμές',
    },
    intro: {
      title: 'Από τι αποτελείται το κόστος ενός μεταλλικού εξαρτήματος',
      body: 'Για ένα εξάρτημα λαμαρίνας ή σωλήνα, το κόστος είναι το άθροισμα του υλικού (βάρος × τιμή ανά κιλό, μαζί με τη φύρα), του χρόνου κοπής (που εξαρτάται από περίμετρο, πάχος και αντοχή του υλικού), των στραντζαρισμάτων, του σεταρίσματος και των εργατικών. Το Workshop Assistant τα υπολογίζει όλα αυτόματα για χάλυβα, ανοξείδωτο και αλουμίνιο.',
    },
    features: {
      title: 'Τι κοστολογεί',
      items: [
        { icon: 'coins', title: 'Υλικό ανά Κιλό', description: 'Βάρος από τη γεωμετρία και την πυκνότητα του υλικού, επί την τιμή ανά κιλό που ορίζετε.' },
        { icon: 'gauge', title: 'Κοπή ανά Υλικό και Πάχος', description: 'Ο χρόνος laser ή ψαλιδιού προσαρμόζεται στην αντοχή εφελκυσμού και το πάχος του υλικού.' },
        { icon: 'boxes', title: 'Στράντζα και Ποσότητες', description: 'Αυτόματος εντοπισμός στραντζαρισμάτων από STEP και κόστος σεταρίσματος που μοιράζεται στην ποσότητα της παρτίδας.' },
        { icon: 'excel', title: 'Προσφορά ανά Τεμάχιο', description: 'Αναφορά Excel με κόστος και τιμή για κάθε εξάρτημα και για όλη την παραγγελία.' },
      ],
    },
    faq: [
      { q: 'Ποια μέταλλα υποστηρίζονται;', a: 'Χάλυβας, ανοξείδωτος χάλυβας και αλουμίνιο, με πυκνότητα, τιμή ανά κιλό και αντοχή εφελκυσμού που ρυθμίζετε εσείς.' },
      { q: 'Κοστολογεί και σωλήνες ή προφίλ;', a: 'Ναι, η κοστολόγηση laser σωλήνα υπολογίζει το κόστος κοπής μεταλλικών σωλήνων και προφίλ με την ίδια λογική.' },
      { q: 'Κοστολογεί μηχανουργικά εξαρτήματα από συμπαγές μέταλλο;', a: 'Όχι. Το Workshop Assistant κοστολογεί εξαρτήματα λαμαρίνας και σωλήνα. Δεν υπολογίζει κόστος τόρνευσης ή 3-αξονικής κατεργασίας σε κέντρο κατεργασίας.' },
      { q: 'Πόσο ακριβής είναι η κοστολόγηση;', a: 'Όσο ακριβείς είναι οι τιμές που ορίζετε. Το πρόγραμμα παίρνει τις διαστάσεις από το πραγματικό σχέδιο και εφαρμόζει τις δικές σας τιμές υλικού, μηχανών και εργατικών.' },
    ],
    related: 'Δείτε επίσης',
    cta: { title: 'Κοστολογήστε τα Μεταλλικά σας Εξαρτήματα', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και δείτε το κόστος κάθε εξαρτήματος σε λεπτά.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Metal Parts Costing Software',
      subtitle: 'Know what every metal part costs before you price it: material weight, laser or guillotine cutting, press brake bending, setup and margin, straight from DXF or STEP.',
      cta1: 'Download Free Trial',
      cta2: 'View Pricing',
    },
    intro: {
      title: 'What makes up the cost of a metal part',
      body: 'For a sheet metal or tube part, the cost is the sum of material (weight × price per kg, plus scrap), cutting time (driven by perimeter, thickness and material strength), bends, setup and labor. Workshop Assistant calculates all of it automatically for steel, stainless steel and aluminum.',
    },
    features: {
      title: 'What it costs',
      items: [
        { icon: 'coins', title: 'Material by Weight', description: 'Weight from the part geometry and material density, multiplied by the price per kg you set.' },
        { icon: 'gauge', title: 'Cutting by Material and Thickness', description: 'Laser or guillotine time adjusts to the material\'s tensile strength and thickness.' },
        { icon: 'boxes', title: 'Bending and Batch Size', description: 'Bends detected automatically from STEP files, and setup cost spread across the batch quantity.' },
        { icon: 'excel', title: 'Per-Part Quote', description: 'An Excel report with the cost and price of every part and the whole order.' },
      ],
    },
    faq: [
      { q: 'Which metals are supported?', a: 'Steel, stainless steel and aluminum, with density, price per kg and tensile strength that you set.' },
      { q: 'Does it cost tubes and profiles too?', a: 'Yes, tube laser costing calculates the cutting cost of metal tubes and profiles with the same method.' },
      { q: 'Does it cost machined parts made from solid metal?', a: 'No. Workshop Assistant costs sheet metal and tube parts. It does not estimate turning or 3-axis machining on a machining centre.' },
      { q: 'How accurate is the costing?', a: 'As accurate as the rates you enter. The program takes dimensions from the actual drawing and applies your own material, machine and labor rates.' },
    ],
    related: 'Related',
    cta: { title: 'Cost Your Metal Parts', subtitle: 'Download the free trial and see what every part costs in minutes.', btn: 'Download Free Trial' },
  },
}

const related = {
  el: [
    { name: 'Λογισμικό Εκτίμησης Κόστους Κατασκευής', path: '/manufacturing-cost-estimator' },
    { name: 'Κοστολόγηση Laser', path: '/laser-costing' },
    { name: 'Κοστολόγηση Σωλήνα Laser', path: '/tube-laser-costing' },
    { name: 'Δωρεάν Υπολογιστής Κόστους', path: '/cost-calculator' },
  ],
  en: [
    { name: 'Manufacturing Cost Estimator', path: '/manufacturing-cost-estimator' },
    { name: 'Laser Costing', path: '/laser-costing' },
    { name: 'Tube Laser Costing', path: '/tube-laser-costing' },
    { name: 'Free Cost Calculator', path: '/cost-calculator' },
  ],
}

const icons = { coins: <Coins className="h-10 w-10" />, gauge: <Gauge className="h-10 w-10" />, boxes: <Boxes className="h-10 w-10" />, excel: <FileSpreadsheet className="h-10 w-10" /> }

const MetalPartsCosting = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Metal Parts Costing Software"
        description="Metal parts costing software: calculate material, laser cutting, bending, setup and margin for steel, stainless and aluminum parts from DXF/STEP files. Free trial."
        path="/metal-parts-costing"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/pricing" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
            <p className="text-lg text-gray-600">{t.intro.body}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/calculator.png?v=2`} alt={t.hero.title} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
          <h2 className="text-2xl font-bold mt-12 mb-4">{t.related}</h2>
          <ul className="space-y-2">
            {related[lang].map((r) => (
              <li key={r.path}>
                <Link to={r.path} className="inline-flex items-center gap-2 font-semibold text-primary-600 hover:text-primary-700">
                  {r.name}<ArrowRight className="h-4 w-4" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default MetalPartsCosting
